import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Stack,
  Paper,
  Alert,
  Button,
  CircularProgress,
  Fade,
} from '@mui/material';
import {
  Business as BusinessIcon,
  CheckCircle as CheckCircleIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuthContext } from '../hooks/useAuth';
import { useOrganization } from '../hooks/useOrganization';
import OrganizationCreationModal from '../components/organization/OrganizationCreationModal';
import type { OrganizationCreationResult } from '../components/organization/OrganizationCreationModal';
import { usePageTitle } from '../hooks/usePageTitle';
import logger from '../utils/logger';

/**
 * Phase 4: 組織作成ページ
 * メール認証完了後に表示される組織作成専用ページ
 */
const OrganizationCreationPage: React.FC = () => {
  usePageTitle('組織作成');
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthContext();
  const {
    shouldCreateOrganization,
    hasExistingOrganization,
    completeOrganizationSetup,
    isLoading
  } = useOrganization();

  // ページ状態
  const [showModal, setShowModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [creationSuccess, setCreationSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * 認証状態とアクセス権限のチェック
   */
  useEffect(() => {
    if (!isAuthenticated || !user) {
      logger.warn('[OrganizationCreation] User not authenticated, redirecting to login');
      navigate('/login');
      return;
    }

    // 既に組織を持っている場合はダッシュボードにリダイレクト
    if (hasExistingOrganization) {
      logger.info('[OrganizationCreation] User already has organization, redirecting to dashboard');
      navigate('/dashboard');
      return;
    }

    // 組織作成が必要な場合は2秒後にモーダルを表示
    if (shouldCreateOrganization) {
      const timer = setTimeout(() => {
        setShowModal(true);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [isAuthenticated, user, hasExistingOrganization, shouldCreateOrganization, navigate]);

  /**
   * 組織作成成功ハンドラー
   */
  const handleOrganizationCreationSuccess = useCallback(async (result: OrganizationCreationResult) => {
    try {
      setIsCreating(true);
      setError(null);

      logger.log('✅ [OrganizationCreation] Organization created successfully:', result.organizationId);

      // 組織セットアップの完了
      const setupSuccess = await completeOrganizationSetup(result.organizationId || '');

      if (setupSuccess) {
        setCreationSuccess(true);
        setShowModal(false);

        // 3秒後にダッシュボードへリダイレクト
        setTimeout(() => {
          navigate('/dashboard', {
            state: {
              welcomeMessage: '組織の作成が完了しました！SalonHubへようこそ！',
              isNewOrganization: true
            }
          });
        }, 3000);
      } else {
        throw new Error('組織セットアップの完了に失敗しました');
      }
    } catch (error) {
      logger.error('❌ [OrganizationCreation] Setup failed:', error);
      setError('組織の設定に失敗しました。もう一度お試しください。');
      setIsCreating(false);
    }
  }, [completeOrganizationSetup, navigate]);

  /**
   * モーダルクローズハンドラー
   */
  const handleModalClose = useCallback(() => {
    if (!isCreating) {
      setShowModal(false);
    }
  }, [isCreating]);

  /**
   * 手動で組織作成を開始
   */
  const handleStartCreation = useCallback(() => {
    setShowModal(true);
    setError(null);
  }, []);

  /**
   * ローディング中の表示
   */
  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, rgba(44, 85, 48, 0.05) 0%, rgba(139, 195, 74, 0.05) 50%, rgba(255, 255, 255, 1) 100%)',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  /**
   * 組織作成成功画面
   */
  if (creationSuccess) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, rgba(44, 85, 48, 0.05) 0%, rgba(139, 195, 74, 0.05) 50%, rgba(255, 255, 255, 1) 100%)',
          p: 2,
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <Paper
              elevation={4}
              sx={{
                borderRadius: 3,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                p: 6,
                textAlign: 'center',
              }}
            >
              <Stack spacing={4} alignItems="center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                >
                  <CheckCircleIcon
                    sx={{
                      fontSize: 100,
                      color: 'success.main',
                    }}
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                >
                  <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
                    組織作成完了！
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    SalonHubへようこそ！
                  </Typography>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                >
                  <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500 }}>
                    組織の作成が正常に完了しました。
                    あなたは自動的にオーナーとして設定され、すべての機能をご利用いただけます。
                    まもなくダッシュボードに移動します...
                  </Typography>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                >
                  <CircularProgress size={40} sx={{ color: 'success.main' }} />
                </motion.div>
              </Stack>
            </Paper>
          </motion.div>
        </Container>
      </Box>
    );
  }

  /**
   * メイン組織作成画面
   */
  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, rgba(44, 85, 48, 0.05) 0%, rgba(139, 195, 74, 0.05) 50%, rgba(255, 255, 255, 1) 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <Paper
            elevation={4}
            sx={{
              borderRadius: 3,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 6 }}>
              <Stack spacing={4} alignItems="center" textAlign="center">
                {/* ヘッダーセクション */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  <BusinessIcon
                    sx={{
                      fontSize: 80,
                      color: 'primary.main',
                      mb: 2,
                    }}
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                >
                  <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', mb: 2 }}>
                    SalonHubへようこそ！
                  </Typography>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                    {user?.name}さん、メール認証が完了しました
                  </Typography>
                </motion.div>

                {/* 説明セクション */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6, duration: 0.5 }}
                  style={{ maxWidth: 600 }}
                >
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    まずは、あなたの美容サロンの組織を作成しましょう。
                    組織を作成することで、顧客管理、予約管理、スタッフ管理など、
                    SalonHubの全機能をご利用いただけるようになります。
                  </Typography>
                </motion.div>

                {/* エラー表示 */}
                {error && (
                  <Fade in={Boolean(error)}>
                    <Alert severity="error" sx={{ width: '100%', maxWidth: 500 }}>
                      {error}
                    </Alert>
                  </Fade>
                )}

                {/* アクションボタン */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleStartCreation}
                    disabled={isCreating}
                    endIcon={<ArrowForwardIcon />}
                    sx={{
                      py: 2,
                      px: 4,
                      borderRadius: 2,
                      fontSize: '1.1rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      minWidth: 200,
                    }}
                  >
                    組織を作成する
                  </Button>
                </motion.div>

                {/* 補助情報 */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1.0, duration: 0.5 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    組織作成は無料で、いつでも設定を変更できます
                  </Typography>
                </motion.div>
              </Stack>
            </Box>
          </Paper>
        </motion.div>
      </Container>

      {/* 組織作成モーダル */}
      <OrganizationCreationModal
        open={showModal}
        onClose={handleModalClose}
        onSuccess={handleOrganizationCreationSuccess}
        userInfo={{
          name: user?.name || '',
          email: user?.email || ''
        }}
      />
    </Box>
  );
};

export default OrganizationCreationPage;